import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";

export type EditableTask = {
  id: number | string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  category: string | null;
  due_date: string | null;
  people?: { id: number | string; name?: string }[];
  vendors?: { id: number | string; name?: string }[];
};

export function useDeleteTask() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await api(`/api/operations_tasks/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { detail?: string; error?: string };
        throw new Error(j.detail || j.error || `HTTP ${res.status}`);
      }
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["operations_tasks"] });
    },
  });
}

export type AttachmentRow = {
  id: number | string;
  task_id: number | string;
  file_name: string;
  content_type: string | null;
  size_bytes: number | null;
  created_at: string;
};

export function useTaskAttachments(taskId: number | string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["task_attachments", String(taskId ?? "")],
    enabled: taskId !== undefined && taskId !== null && taskId !== "",
    queryFn: async (): Promise<AttachmentRow[]> => {
      const res = await api(`/api/operations_tasks/${taskId}/attachments`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as AttachmentRow[];
    },
    staleTime: 30_000,
  });
}

export function useUploadAttachment() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      file,
    }: {
      taskId: number | string;
      file: File;
    }): Promise<AttachmentRow> => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api(`/api/operations_tasks/${taskId}/attachments`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { detail?: string; error?: string };
        throw new Error(j.detail || j.error || `HTTP ${res.status}`);
      }
      return (await res.json()) as AttachmentRow;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["task_attachments", String(vars.taskId)] });
    },
  });
}

export function useDeleteAttachment() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await api(`/api/attachments/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { detail?: string; error?: string };
        throw new Error(j.detail || j.error || `HTTP ${res.status}`);
      }
      return true;
    },
    onSuccess: () => {
      // We don't know which task the attachment belonged to, so invalidate all
      // attachments queries.
      qc.invalidateQueries({ queryKey: ["task_attachments"] });
    },
  });
}
